import { ICreateApp } from "../../../../App/Typings/AbacApp";
import { useCreateAbacAppMutation } from "../../AbacAppQueries";
import { useCallback } from "react";
import { object, string, ObjectSchema, bool } from "yup";
import { useFormik } from "formik";
import { useNotification } from "../../../../App/State/Hooks/useNotificationHooks";
import { useModalHooks } from "../../../../App/State/Hooks/useModalHooks";

export default function useCreateAppFormState() {
  const createAppSuccessMessage = "New application created for ABAC!";
  const createAppErrorMessage = "Error creating new application for ABAC.";
  const { closeModal } = useModalHooks();
  const { displaySuccess, displayError } = useNotification();
  const { mutate: createNewApp, isLoading: createIsLoading } =
    useCreateAbacAppMutation();

  //Validation for the create app form
  const validationSchema = object().shape({
    appName: string()
      .trim()
      .max(100, "App Name must be less than 100 characters")
      .required("App Name is required"),
    techStack: string()
      .trim()
      .max(50, "Tech Stack must be less than 50 characters"),
    appDescription: string().max(
      500,
      "App Description must be less than 500 characters"
    ),
  });

  //Initial create app formik setup
  const formik = useFormik({
    initialValues: {
      appName: "",
      techStack: "",
      appDescription: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      let newApp: ICreateApp = {
        appName: values.appName.trim(),
        techStack: values.techStack.trim(),
        appDescription: values.appDescription.trim(),
        isAdmin: false,
      };
      createApp(newApp);
    },
  });

  //Initial create app formik setup
  const handleCreateModalClose = () => {
    if (!createIsLoading) {
      closeModal();
      formik.resetForm();
    } else return;
  };

  // Create a new ABAC application
  const createApp = useCallback(
    function submitApp(newApp: ICreateApp) {
      if (!createIsLoading) {
        createNewApp(newApp, {
          onError: () => {
            displayError(createAppErrorMessage);
          },
          onSuccess: () => {
            displaySuccess(createAppSuccessMessage);
            closeModal();
            formik.resetForm();
          },
        });
      }
    },
    [
      createNewApp,
      createIsLoading,
      displayError,
      displaySuccess,
      closeModal,
      formik,
    ]
  );

  return {
    getters: {
      createIsLoading,
      formik,
    },
    handlers: { handleCreateModalClose },
  };
}
